// src/pages/FornitoreOrdineDettaglio.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  getSupplierOrderDetail,
  updateSupplierOrderStatus,
} from "../../api/ordersApi";
import "../css/Ordini.css";
import AddToCalendarButtons from "../../components/orders/AddToCalendarButtons";

/* ---------- utils ---------- */
function formatCurrency(v) {
  const n = Number(v);
  const isCents = Number.isInteger(n) && n >= 50;
  const eur = isCents ? n / 100 : n;
  return eur.toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("it-IT");
}

function formatDateLabel(dateIso) {
  if (!dateIso) return "—";
  return formatDate(dateIso);
}

function formatDateWithSlot(dateIso, slot) {
  const d = formatDateLabel(dateIso);
  if (!slot || slot === "—") return d;
  return `${d} · ${slot}`;
}

function safeString(v) {
  return String(v || "").trim();
}

function buildGoogleMapsUrl(address) {
  const q = encodeURIComponent(safeString(address));
  return q ? `https://www.google.com/maps/search/?api=1&query=${q}` : "";
}

function buildAppleMapsUrl(address) {
  const q = encodeURIComponent(safeString(address));
  return q ? `http://maps.apple.com/?q=${q}` : "";
}

function buildPreferredMapsUrl(address) {
  const raw = safeString(address);
  if (!raw) return "";

  // iPhone/iPad: Apple Maps
  const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
  const isiOS = /iPhone|iPad|iPod/i.test(ua);

  return isiOS ? buildAppleMapsUrl(raw) : buildGoogleMapsUrl(raw);
}

function normalizePhoneForTel(phone) {
  const raw = safeString(phone);
  if (!raw) return "";

  const cleaned = raw.replace(/[^\d+]/g, "");
  if (!cleaned) return "";

  // conserva + iniziale se presente, altrimenti solo cifre
  if (cleaned.startsWith("+")) {
    return `tel:${cleaned}`;
  }

  const digits = cleaned.replace(/\+/g, "");
  return digits ? `tel:${digits}` : "";
}

const isCrossSell = (item) => Number(item?.id_servizio) === 0;

const DB_STATUS_OPTIONS = [
  { value: "pagata", label: "Pagata" },
  { value: "ritirata", label: "Ritirata" },
  { value: "completata", label: "Completata" },
  { value: "in_consegna", label: "In Consegna" },
  { value: "consegnata", label: "Consegnato" },
];

function humanize(raw) {
  switch (raw) {
    case "in_attesa":
      return "In attesa";
    case "accettata":
      return "Accettata";
    case "pagata":
      return "Pagata";
    case "ritirata":
      return "Ritirata";
    case "completata":
      return "Completata";
    case "in_consegna":
      return "In consegna";
    case "consegnata":
      return "Consegnato";
    case "annullata":
      return "Annullata";
    case "rifiutata":
      return "Rifiutata";
    default:
      return "In lavorazione";
  }
}

export default function FornitoreOrdineDettaglio() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const fornitore = searchParams.get("fornitore") || "";

  const backParams = new URLSearchParams();
  if (fornitore) backParams.set("fornitore", fornitore);
  ["status", "from", "to", "page"].forEach((k) => {
    const v = searchParams.get(k);
    if (v) backParams.set(k, v);
  });

  const backHref = `/fornitore/ordini${backParams.toString() ? `?${backParams.toString()}` : ""
    }`;

  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);

    getSupplierOrderDetail(id, { fornitore })
      .then((data) => {
        if (!alive) return;
        setOrder(data);
      })
      .catch(() => toast.error("Impossibile caricare l'ordine"))
      .finally(() => alive && setLoading(false));

    return () => {
      alive = false;
    };
  }, [id, fornitore]);

  const items = useMemo(() => order?.items || [], [order]);
  const hasRows = items.length > 0;

  const pickupAddress = order?.pickup_address || null;
  const fullAddress = safeString(pickupAddress?.full_address);
  const mapsUrl = buildPreferredMapsUrl(fullAddress);
  const telHref = normalizePhoneForTel(pickupAddress?.phone);
  const isOrgPickup = order?.address_source_type === "organization";

  async function handleStatusChange(e) {
    const next = e.target.value;
    if (!order) return;
    const prev = order.raw_status;

    try {
      setOrder((o) => ({ ...o, raw_status: next, status: humanize(next) }));
      await updateSupplierOrderStatus(id, next, { fornitore });
      toast.success("Stato aggiornato");
    } catch (err) {
      setOrder((o) => ({ ...o, raw_status: prev, status: humanize(prev) }));
      toast.error(err?.message || "Errore aggiornamento stato");
    }
  }

  const statusClass =
    order?.raw_status && !loading
      ? `order-meta-status order-meta-status--${order.raw_status}`
      : "order-meta-status";

  async function handleCopyAddress() {
    if (!fullAddress) {
      toast.error("Indirizzo non disponibile");
      return;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(fullAddress);
      } else {
        const ta = document.createElement("textarea");
        ta.value = fullAddress;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }

      toast.success("Indirizzo copiato");
    } catch {
      toast.error("Impossibile copiare l'indirizzo");
    }
  }

  function handleOpenMaps() {
    if (!mapsUrl) {
      toast.error("Indirizzo non disponibile");
      return;
    }

    window.open(mapsUrl, "_blank", "noopener,noreferrer");
  }

  function handleCallCustomer() {
    if (!telHref) {
      toast.error("Telefono non disponibile");
      return;
    }

    window.location.href = telHref;
  }

  return (
    <div className="list-page list-page--orderdetail list-page--supplierdetail">
      <header className="list-header list-header--with-back">
        <div className="list-header-main">
          <Link
            className="btn btn-ghost btn-icon list-header-back"
            to={backHref}
            aria-label="Indietro"
          >
            ←
          </Link>

          <div>
            <h1>ordine_{id}</h1>
            <p className="hint" style={{ marginTop: 2 }}>
              Dettaglio ordine lato fornitore
            </p>
          </div>
        </div>
      </header>

      <section className="list-filters">
        <div className="list-filterbar list-filterbar--summary">
          <div className="list-filter-field ods-field ods-field--supplier">
            <label className="list-filter-label">Cliente</label>
            <div className="ods-value">
              {order?.cliente
                ? isOrgPickup && order.cliente_phone
                  ? `${order.cliente} · ${order.cliente_phone}`
                  : order.cliente
                : loading
                  ? "…"
                  : "—"}
            </div>
          </div>

          <div className="list-filter-field ods-field ods-field--date">
            <label className="list-filter-label">Data ordine</label>
            <div className="ods-value">
              {order?.created_at ? formatDate(order.created_at) : loading ? "…" : "—"}
            </div>
          </div>

          <div className="list-filter-field ods-field ods-field--nr">
            <label className="list-filter-label">Nr ordine</label>
            <div className="ods-orderid">{id}</div>
          </div>

          <div className="list-filter-field ods-field ods-field--status">
            <label className="list-filter-label">Stato</label>
            <div className="ods-status-pill">
              {order?.status ?? (loading ? "…" : "—")}
            </div>
          </div>
        </div>
      </section>

      <section className="list-filters supplier-detail-extra">
        <div className="list-filterbar supplier-detail-extra-grid">
          <div className="list-filter-field">
            <label className="list-filter-label">Totale</label>
            <div className="ods-value">
              {order ? formatCurrency(order.total_cents) : loading ? "…" : "—"}
            </div>
          </div>

          <div className="list-filter-field">
            <label className="list-filter-label">Ritiro</label>
            <div className="ods-value">
              {order
                ? formatDateWithSlot(order.data_ritiro, order.ritiro_fascia)
                : loading
                  ? "…"
                  : "—"}
            </div>
          </div>

          <div className="list-filter-field">
            <label className="list-filter-label">Consegna</label>
            <div className="ods-value">
              {order
                ? formatDateWithSlot(order.data_consegna, order.consegna_fascia)
                : loading
                  ? "…"
                  : "—"}
            </div>
          </div>

          <div className="list-filter-field">
            <label className="list-filter-label">Stato ordine</label>
            <div>
              <select
                className="supplier-order-meta-select"
                value={order?.raw_status || ""}
                onChange={handleStatusChange}
                disabled={!order || loading}
              >
                {!order && <option>…</option>}
                {DB_STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="list-filter-field supplier-detail-address-field">
            <label className="list-filter-label">
              {isOrgPickup ? "Ritiro/consegna presso" : "Indirizzo ritiro"}
            </label>
            <div className="ods-value">
              {isOrgPickup
                ? pickupAddress?.organization_name || (loading ? "…" : "—")
                : pickupAddress?.full_address || (loading ? "…" : "—")}
            </div>

            {isOrgPickup && !!pickupAddress?.full_address && (
              <small className="hint supplier-detail-subline">
                {pickupAddress.full_address}
              </small>
            )}

            {!!pickupAddress?.phone && (
              <small className="hint supplier-detail-subline">
                Telefono: {pickupAddress.phone}
              </small>
            )}

            {!!pickupAddress?.notes && (
              <small className="hint supplier-detail-subline">
                Note: {pickupAddress.notes}
              </small>
            )}

            <div className="supplier-order-native-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleOpenMaps}
                disabled={!mapsUrl}
              >
                Apri in Maps
              </button>

              <button
                type="button"
                className="btn btn-ghost"
                onClick={handleCopyAddress}
                disabled={!pickupAddress?.full_address}
              >
                Copia indirizzo
              </button>

              <button
                type="button"
                className="btn btn-ghost btn-call-client"
                onClick={handleCallCustomer}
                disabled={!pickupAddress?.phone}
              >
                Chiama cliente
              </button>
            </div>

            <AddToCalendarButtons
              order={order}
              actor="supplier"
              disabled={loading}
              containerClassName="order-calendar-actions order-calendar-actions--supplier"
            />
          </div>
        </div>
      </section>

      <main className="list-main supplier-order-main">
          <div
            className="list-table list-table--details list-table--compact"
            role="table"
            aria-label="Dettaglio ordine fornitore"
          >
            <div className="list-row list-row--head" role="row">
              <div className="list-cell" role="columnheader">
                Articolo
              </div>
              <div className="list-cell list-ta-center" role="columnheader">
                Qta
              </div>
              <div className="list-cell list-ta-right" role="columnheader">
                Prezzo
              </div>
              <div className="list-cell list-ta-right" role="columnheader">
                Totale
              </div>
            </div>

            {loading &&
              Array.from({ length: 4 }).map((_, i) => (
                <div
                  className="list-row list-skel"
                  key={`sk-${i}`}
                  role="row"
                  aria-busy="true"
                >
                  <div className="list-cell">.</div>
                  <div className="list-cell">.</div>
                  <div className="list-cell list-ta-right">.</div>
                  <div className="list-cell list-ta-right">.</div>
                </div>
              ))}

            {!loading && !hasRows && (
              <div className="list-empty">Nessun articolo.</div>
            )}

            {!loading &&
              hasRows &&
              items.map((r) => (
                <div className="list-row" role="row" key={r.id}>
                  <div className="list-cell" role="cell" data-label="Articolo">
                    <span className="list-item-title">
                      {r.descrizione}
                      {isCrossSell(r) && (
                        <span className="list-pill list-pill--cross">
                          cross-sell
                        </span>
                      )}
                    </span>
                    {r.tipo ? (
                      <small style={{ color: "var(--muted)" }}>
                        {" "}
                        · {r.tipo}
                      </small>
                    ) : null}
                  </div>

                  <div
                    className="list-cell list-ta-center"
                    role="cell"
                    data-label="Qta"
                  >
                    {r.quantita}
                  </div>

                  <div
                    className="list-cell list-ta-right"
                    role="cell"
                    data-label="Prezzo"
                  >
                    {formatCurrency(r.unit_price)}
                  </div>

                  <div
                    className="list-cell list-ta-right"
                    role="cell"
                    data-label="Totale"
                  >
                    {formatCurrency(r.line_total)}
                  </div>
                </div>
              ))}
          </div>
      </main>
    </div>
  );
}